import React, { useState } from 'react';
import { X } from 'lucide-react';
import { NewUserInput, UserRole } from '../../types/user';
import { useAdmin } from '../../contexts/adminContext';

interface Props {
  open: boolean;
  onClose: () => void;
  onOpenBulk: () => void;
  darkMode?: boolean;
  backendUrl: string; // Add this prop
  onUserAdded?: () => void; // Optional callback to refresh user list
}

const roles: UserRole[] = ['Admin', 'User'];

const AddUserModal: React.FC<Props> = ({
  open,
  onClose,
  onOpenBulk,
  darkMode = false,
  backendUrl,
  onUserAdded
}) => {
  const [form, setForm] = useState<NewUserInput>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'User',
    status: 'Active',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const {
    fetchRecentMeetings,
    fetchTopUsers,
    fetchAllUserData,
  } = useAdmin();

  if (!open) return null;

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.firstName?.trim()) err.firstName = 'First name is required';
    if (!form.lastName?.trim()) err.lastName = 'Last name is required';
    if (!form.email?.trim()) err.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = 'Invalid email';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const addUser = async (input: NewUserInput) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) throw new Error('Authentication required');

    // Transform frontend data to match backend expectations
    const backendData = {
      first_name: input.firstName,
      last_name: input.lastName,
      email: input.email,
      password: input.password || input.email.split('@')[0] + '123', // Use entered password, fallback to generated
      department: input.department,
      job_title: input.position,
      isAdmin: input.role === 'Admin',
      phone_number: input.phoneNumber,
      location: input.location,
    };

    const response = await fetch(`${backendUrl}/api/admin/add-users`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(backendData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to add user');
    }

    return await response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setApiError(null);

    try {
      const data = await addUser(form);
      console.log('Add user response:', data);
      fetchRecentMeetings();
      fetchTopUsers();
      fetchAllUserData();

      // Reset form
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        role: 'User',
        status: 'Active',
        password: '',
      });

      // Call optional callback to refresh user list
      if (onUserAdded) {
        onUserAdded();
      }

      onClose();
    } catch (err) {
      console.error("Failed to add user", err);
      setApiError(err instanceof Error ? err.message : "Failed to add user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className={`${darkMode ? 'bg-[#232336] text-white border border-white/10' : 'bg-white'} rounded-2xl w-full max-w-lg p-6 shadow-xl`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Add User</h3>
          <button onClick={onClose} aria-label="Close" className={`${darkMode ? 'text-white/70 hover:text-white' : ''}`}>
            <X />
          </button>
        </div>

        {apiError && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300 text-red-700 text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>First Name</label>
              <input
                value={form.firstName || ''}
                onChange={e => setForm({ ...form, firstName: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 ${errors.firstName ? 'border-red-300' : 'focus:border-transparent'} ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
              />
              {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Last Name</label>
              <input
                value={form.lastName || ''}
                onChange={e => setForm({ ...form, lastName: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 ${errors.lastName ? 'border-red-300' : 'focus:border-transparent'} ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
              />
              {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Email</label>
            <input
              type="email"
              value={form.email || ''}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 ${errors.email ? 'border-red-300' : 'focus:border-transparent'} ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
            />
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
              Password
            </label>
            <input
              type="password"
              value={form.password || ''}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 ${errors.password ? 'border-red-300' : 'focus:border-transparent'} ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
            />
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
          </div>


          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Phone Number</label>
              <input
                value={form.phoneNumber || ''}
                onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
              />
            </div>
            <div>
              <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Department</label>
              <input
                value={form.department || ''}
                onChange={e => setForm({ ...form, department: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Position</label>
              <input
                value={form.position || ''}
                onChange={e => setForm({ ...form, position: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
              />
            </div>
            <div>
              <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Location</label>
              <input
                value={form.location || ''}
                onChange={e => setForm({ ...form, location: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : ''}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm mb-1 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Role</label>
              <select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value as UserRole })}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : ''}`}
              >
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div></div>
          </div>

          <div className="flex justify-between gap-3 pt-2">
            <button
              type="button"
              className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : ''}`}
              onClick={onClose}
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : ''}`}
                onClick={onOpenBulk}
              >
                Bulk Add
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 text-white disabled:opacity-60"
              >
                {submitting ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;